import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Stack, Box, Flex, useDisclosure, useMultiStyleConfig} from '@chakra-ui/react'
import {useGigya} from '../../context/gigya'
import GigyaModal from '../modals/gigya-modal'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {setCustomData, addAriaLiveToStatusMessages} from '../../utils/gigya'
import {BRAND_KEY} from '../../brand'
import {useWishList} from '../../hooks/use-wish-list'
import GigyaRegisterController from '../../utils/gigya/registration'
import GigyaBasicFormController from '../../utils/gigya/lite-registration'
import {useIntl} from 'react-intl'
import LoadingSpinner from '../loading-spinner'
import useGigyaSSR from '../../custom-hooks/use-gigya-ssr'
import {Global} from '@emotion/react'

const LoginForm = ({variant = 'default', isSubscription = false}) => {
    const {gigyaObject, gigyaLocale} = useGigya()
    const {addLayer} = useGoogleTagManager()
    const stylesLogin = useMultiStyleConfig('Login', {variant})
    const stylesGlobal = useMultiStyleConfig('GigyaGlobal')
    const intl = useIntl()
    const ssrGigyaMethods = useGigyaSSR()

    const FORGOTTEN_PASSWORD_SETTINGS = {
        screenSet: 'Default-RegistrationLogin',
        startScreen: 'gigya-forgot-password-screen',
        containerID: 'gigya-forgotten-password-container',
        lang: gigyaLocale,
        onAfterScreenLoad: () => {
            addAriaLiveToStatusMessages('gigya-forgotten-password-container')

            const forgotPasswordContainer = document.getElementById(
                'gigya-forgotten-password-container',
            )

            if (forgotPasswordContainer) {
                const emailInput = forgotPasswordContainer.querySelector(
                    'input[data-gigya-name="loginID"][type="text"]',
                )

                if (emailInput) {
                    const wrapper = emailInput.closest('.dlg-gigya-input-text-wrapper')

                    if (wrapper) {
                        const existingLabel = wrapper.querySelector('.gigya-label')
                        if (!existingLabel) {
                            const label = document.createElement('label')
                            label.className = 'gigya-label'
                            label.innerHTML =
                                '<span class="gigya-label-text gigya-required-display">Email*</span>'
                            label.setAttribute('for', emailInput.id || 'loginID')

                            wrapper.insertBefore(label, emailInput)
                        }
                    }
                }
            }
        },
    }

    const {isOpen, onOpen, onClose} = useDisclosure()

    const {data: basket} = useCurrentBasket()
    const {data: wishListData} = useWishList()

    // onAfterSubmit below is registered once per gigyaObject and would otherwise
    // close over a stale basket/wishlist snapshot (e.g. from before an item was added).
    const basketRef = useRef(basket)
    const wishListDataRef = useRef(wishListData)
    useEffect(() => {
        basketRef.current = basket
    }, [basket])
    useEffect(() => {
        wishListDataRef.current = wishListData
    }, [wishListData])

    useEffect(() => {
        if (!gigyaObject) return

        window.DLG = {
            ...(window.DLG || {}),
            EVE: {
                emit: (e) => {
                    if (e === 'GIGYA.LOAD.FORGOTTEN_PASSWORD') {
                        onOpen()
                    }
                    if (e === 'GIGYA.CLOSE.FORGOTTEN_PASSWORD') {
                        onClose()
                    }
                    if (e === 'GIGYA.REDIRECT.LOGIN') {
                        gigyaObject?.accounts?.hideScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                        })
                    }
                },
            },
            gigya: {brand: BRAND_KEY},
        }
        return () => {
            delete window.DLG
        }
    }, [gigyaObject])

    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        if (!gigyaObject) return

        gigyaObject?.accounts.showScreenSet({
            screenSet: 'Default-RegistrationLogin',
            startScreen: 'gigya-login-screen',
            containerID: 'login',
            lang: gigyaLocale,
            deviceType: 'desktop',
            onBeforeScreenLoad: (event) => {
                if (!event.context || event.context != 'ownid-reg-completion') {
                    //For registration completion screen
                    if (
                        event.nextScreen === 'gigya-complete-registration-screen' &&
                        String(event?.response?.errorCode) === '206001'
                    ) {
                        GigyaRegisterController.showCompleteRegistration({
                            gigyaObject,
                            event,
                            gigyaLocale,
                            GigyaRegisterController,
                            GigyaBasicFormController,
                        })

                        return false
                    }
                }

                if (!event.context) {
                    if (
                        event.nextScreen === 'gigya-verification-pending-screen' &&
                        String(event?.response?.errorCode) === '206002'
                    ) {
                        gigyaObject.accounts.showScreenSet({
                            screenSet: 'Default-RegistrationLogin',
                            startScreen: 'gigya-verification-pending-screen',
                            regToken:
                                event?.response?.response?.regToken || event?.response?.regToken,
                            lang: gigyaLocale,
                            onBeforeScreenLoad: () => {
                                const closeBtn = document.querySelector(
                                    '.gigya-screen-dialog-top .gigya-screen-dialog-close a',
                                )

                                const oldImg = closeBtn?.querySelector('img')
                                if (oldImg) {
                                    oldImg.remove()

                                    const svg = document.createElement('span')
                                    svg.innerHTML = `
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M18 6L6 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
                                      <path d="M6 6L18 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                  `
                                    closeBtn.appendChild(svg)
                                }

                                const modalTitle = document.querySelector(
                                    '.gigya-screen-dialog-top .gigya-screen-dialog-caption',
                                )

                                if (modalTitle) {
                                    modalTitle.style.display = 'none'

                                    const customTitle = document.createElement('div')
                                    customTitle.className = modalTitle.className
                                    customTitle.id = 'custom-modal-title'

                                    customTitle.textContent = intl.formatMessage({
                                        id: 'screensets.registration_pending.title',
                                        defaultMessage: 'Your account is almost ready',
                                    })

                                    modalTitle.parentNode.insertBefore(customTitle, modalTitle)
                                }
                            },
                        })
                        return false
                    }
                }
            },
            onAfterScreenLoad: () => {
                setIsLoading(false)

                const modalContainer = document.querySelector('.gigya-screen-dialog')
                if (modalContainer) {
                    modalContainer.style.display = 'none'
                    void modalContainer.offsetHeight
                    modalContainer.style.display = ''
                }

                const emailInput = document.querySelector('input[type="text"]')
                if (emailInput) {
                    emailInput.id = 'bx-element-2246463-L0c12Bj-input'
                }

                const headerElement = document.querySelector(
                    '#login .gigya-composite-control.gigya-composite-control-header.dln-loginForm__paragraph',
                )

                if (headerElement) {
                    const label = document.createElement('p')
                    label.className = 'required-fields-label'
                    label.style.marginTop = '16px'
                    label.style.fontSize = '12px'
                    label.style.color = 'var(--chakra-colors-text-secondary)'
                    label.textContent = intl.formatMessage({
                        id: 'login.required_fields_label',
                        defaultMessage: 'Asterisks * indicate required fields',
                    })
                    headerElement.appendChild(label)
                }

                const socialTitleEl = document.querySelector(
                    '#login .gigya-composite-control.gigya-composite-control-header.dln-registration__paragraph',
                )
                if (socialTitleEl) socialTitleEl.style.display = 'none'

                const loginFormHeader = document.querySelector(
                    '#login .gigya-composite-control.gigya-composite-control-header.dln-loginForm__paragraph',
                )
                if (loginFormHeader && !loginFormHeader.querySelector('#login-subtitle')) {
                    ;[...loginFormHeader.childNodes].forEach((node) => {
                        if (
                            node.nodeType === Node.TEXT_NODE ||
                            (node.nodeType === Node.ELEMENT_NODE && node.id !== 'login-subtitle')
                        ) {
                            node.style
                                ? (node.style.display = 'none')
                                : loginFormHeader.removeChild(node)
                        }
                    })
                    const subtitle = document.createElement('p')
                    subtitle.id = 'login-subtitle'
                    subtitle.textContent = intl.formatMessage({
                        id: 'login.subtitle',
                        defaultMessage: "Effettua l'accesso con le tue credenziali",
                    })
                    loginFormHeader.insertBefore(subtitle, loginFormHeader.firstChild)
                }

                const loginForm = document.querySelector('#login form')
                const submitRow = loginForm?.querySelector('.gigya-layout-row.with-divider')
                const socialControl = loginForm?.querySelector(
                    '.gigya-layout-row .gigya-composite-control-social-login',
                )
                if (submitRow && socialControl) {
                    socialControl.parentElement.insertBefore(submitRow, socialControl)
                }

                if (socialControl && !document.getElementById('login-social-separator')) {
                    const sep = document.createElement('p')
                    sep.id = 'login-social-separator'
                    sep.textContent = intl.formatMessage({
                        id: 'login.social_separator',
                        defaultMessage:
                            'oppure entra rapidamente con i tuoi account di social media',
                    })
                    socialControl.parentElement.insertBefore(sep, socialControl)
                }

                const buildCustomSocialButtons = () => {
                    const uiContainer = document.querySelector('#login [id^="login_social_"]')
                    if (!uiContainer || document.getElementById('login-social-custom')) return

                    const providers = [...uiContainer.querySelectorAll('.gigya-login-provider')]
                    if (!providers.length) return

                    const providerMeta = {
                        facebook: {
                            label: 'Facebook',
                            icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" fill="#1877F2"/></svg>`,
                        },
                        googleplus: {
                            label: 'Google',
                            icon: `<svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>`,
                        },
                    }

                    const row = document.createElement('div')
                    row.id = 'login-social-custom'

                    providers.forEach((provSpan) => {
                        const providerName = provSpan.getAttribute('data-gigya-provider') || ''

                        const meta = providerMeta[providerName] || {label: providerName, icon: ''}
                        const btn = document.createElement('button')
                        btn.type = 'button'
                        btn.className = 'login-social-btn'
                        btn.innerHTML = `<span style="display:flex;align-items:center;line-height:0">${meta.icon}</span><span>${meta.label}</span>`
                        btn.addEventListener('click', () => {
                            const trigger = provSpan.querySelector('button') || provSpan
                            trigger.click()
                        })
                        row.appendChild(btn)
                    })

                    uiContainer.style.setProperty('display', 'none', 'important')
                    uiContainer.parentElement.appendChild(row)
                }
                buildCustomSocialButtons()
                setTimeout(buildCustomSocialButtons, 300)
            },
            onBeforeValidation: () => {},
            onBeforeSubmit: () => {
                gigyaObject && gigyaObject.accounts && gigyaObject.accounts.logout()
            },
            onSubmit: (e) => setCustomData(e),
            onAfterSubmit: async (res) => {
                const brands = res?.response?.data?.brands || {}
                GigyaRegisterController.onAfterSubmit(res, ssrGigyaMethods, brands)
                // Apply the class to each form with class gigya-login-form
                const forms = document.querySelectorAll('form.gigya-login-form')
                forms.forEach((form) => {
                    form.classList.add('bx-row-submit-default', 'bx-button')
                })
                localStorage.setItem('guestBasket', JSON.stringify(basketRef.current))
                localStorage.setItem('guestWishList', JSON.stringify(wishListDataRef.current))
                if (res?.response && res?.response?.status !== 'FAIL') {
                    let eventLabel = 'login'
                    if (variant === 'productRegistration') {
                        eventLabel =
                            res.form === 'div.gigya-social-login'
                                ? 'user_detail-login-social'
                                : 'user_detail-login-email'
                    }
                    if (BRAND_KEY === 'kenwood' && variant !== 'productRegistration') {
                        eventLabel = res?.response?.operation?.includes('/accounts.socialLogin')
                            ? 'social'
                            : res?.response?.operation?.includes('/accounts.login')
                            ? 'email'
                            : 'login'
                    }
                    await addLayer(
                        basic_gtm_event.bind(this, {
                            event:
                                variant === 'productRegistration'
                                    ? 'productRegistration'
                                    : variant === 'preCheckout'
                                    ? 'checkoutAccess'
                                    : 'reservedArea',
                            eventCategory:
                                variant === 'productRegistration'
                                    ? 'afterSales'
                                    : BRAND_KEY === 'kenwood'
                                    ? 'login'
                                    : 'loyalty',
                            eventAction:
                                variant === 'productRegistration'
                                    ? 'productRegistration'
                                    : variant === 'preCheckout'
                                    ? 'checkout'
                                    : 'reservedArea',
                            eventLabel,
                            ...(variant === 'preCheckout' && {
                                method: res?.response?.operation?.includes('/accounts.socialLogin')
                                    ? 'social'
                                    : res?.response?.operation?.includes('/accounts.login')
                                    ? 'email'
                                    : res?.response?.operation?.includes('notifyLogin')
                                    ? 'faceId'
                                    : 'login_method_placeholder',
                            }),
                        }),
                    )
                }
            },
            onError: () => {},
            onFieldChanged: () => {},
        })

        gigyaObject?.accounts.showScreenSet(FORGOTTEN_PASSWORD_SETTINGS)
    }, [gigyaObject])

    return (
        <>
            <Global styles={stylesGlobal['.chakra-ui-light']} />{' '}
            <Stack
                {...(isSubscription ? stylesLogin.loginStackSub : stylesLogin.loginStack)}
                __css={stylesLogin}
            >
                {isLoading && (
                    <Box position="relative" w="100%" minH="200px">
                        <Flex
                            position="absolute"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            align="center"
                            justify="center"
                        >
                            <LoadingSpinner />
                        </Flex>
                    </Box>
                )}
                <div id="login"></div>
                <GigyaModal onClose={onClose} isOpen={isOpen}>
                    <div id="gigya-forgotten-password-container"></div>
                </GigyaModal>
            </Stack>
        </>
    )
}

LoginForm.propTypes = {
    variant: PropTypes.string,
    isSubscription: PropTypes.bool,
}

export default LoginForm
